import { useState, useEffect } from 'react';

// Define TypeScript interfaces for data structure
interface ColumnHeader {
  column_id: string;
  label: string;
  order: number;
  data_type?: string;
  is_required?: boolean;
  options?: any[];
}

interface RowHeader {
  row_id: string;
  order: number;
  label: string;
}

interface TableValue {
  row_id: string;
  row_order: number;
  column_id: string;
  column_order: number;
  value: string;
}

interface TableData {
  table_id?: string;
  label?: string;
  order?: number;
  columnHeaders: ColumnHeader[];
  rowHeaders?: RowHeader[];
  values?: TableValue[];
  columnGroups?: any[];
  additional_config?: {
    dimensions?: {
      columns: number;
      rows: number;
    };
    headers?: {
      rowHeader: boolean;
      columnHeader: boolean;
    };
    columnGroupingEnabled?: boolean;
    additionalRows?: boolean;
  };
}

// Function to format value based on data type
const formatValue = (value: string) => {
  const numValue = parseFloat(value);
  if (!isNaN(numValue)) {
    // Format as currency if it's a number
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numValue);
  }
  return value;
};

const DynamicTable = ({ tableData }: { tableData?: TableData }) => {
  const [sortedData, setSortedData] = useState<TableValue[]>([]);
  const [sortConfig, setSortConfig] = useState({ columnId: '', direction: 'asc' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tableData && Array.isArray(tableData.values)) {
      setSortedData([...tableData.values]);
    } else {
      setSortedData([]);
    }
    setLoading(false);
  }, [tableData]);

  // Sort data by column
  const sortData = (columnId: string) => {
    if (!tableData?.values || !Array.isArray(tableData.values)) return;

    let direction = 'asc';
    if (sortConfig.columnId === columnId && sortConfig.direction === 'asc') {
      direction = 'desc';
    }

    const newSortedData = [...tableData.values].sort((a, b) => {
      if (a.column_id === columnId && b.column_id === columnId) {
        const aValue = parseFloat(a.value);
        const bValue = parseFloat(b.value);

        if (!isNaN(aValue) && !isNaN(bValue)) {
          return direction === 'asc' ? aValue - bValue : bValue - aValue;
        } else {
          return direction === 'asc'
            ? a.value.localeCompare(b.value)
            : b.value.localeCompare(a.value);
        }
      }
      return a.column_order - b.column_order;
    });

    setSortedData(newSortedData);
    setSortConfig({ columnId, direction });
  };

  // Function to get the sort direction indicator
  const getSortDirectionIndicator = (columnId: string) => {
    if (sortConfig.columnId !== columnId) return null;
    return sortConfig.direction === 'asc' ? ' ↑' : ' ↓';
  };

  // Group values by row_id for easy rendering
  const groupValuesByRow = () => {
    const groupedValues: Record<string, TableValue[]> = {};

    sortedData.forEach((value) => {
      if (!groupedValues[value.row_id]) {
        groupedValues[value.row_id] = [];
      }
      groupedValues[value.row_id].push(value);
    });

    return groupedValues;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500">Loading data...</div>
      </div>
    );
  }

  if (!tableData) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500">No table data provided</div>
      </div>
    );
  }

  if (!Array.isArray(tableData.values) || tableData.values.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500">No data available</div>
      </div>
    );
  }

  // Sort column headers by order
  const sortedColumnHeaders = [...tableData.columnHeaders].sort((a, b) => a.order - b.order);
  const groupedValues = groupValuesByRow();
  const sortedRowHeaders = tableData.rowHeaders
    ? [...tableData.rowHeaders].sort((a, b) => a.order - b.order)
    : [];

  return (
    <div className="max-w-full mx-auto">
      <div className="bg-white  overflow-hidden shadow-md">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#E5EAFE]">
              <tr>
                {/* Row header column */}
                {tableData.rowHeaders && tableData.rowHeaders.length > 0 && (
                  <th className="px-6 py-3 border-r text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                    Health care type
                  </th>
                )}

                {/* Column headers */}
                {sortedColumnHeaders.map((header) => (
                  <th
                    key={header.column_id}
                    className="px-6 py-3 text-left border-r text-sm font-semibold bg-[#E5EAFE] text-ds-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-200"
                    onClick={() => sortData(header.column_id)}
                  >
                    <div className="flex items-center text-nowrap">
                      {header.label}
                      {getSortDirectionIndicator(header.column_id)}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedRowHeaders.map((rowHeader, rowIndex) => {
                const rowValues = groupedValues[rowHeader.row_id] || [];

                return (
                  <tr
                    key={rowHeader.row_id}
                    className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                  >
                    {/* Row header cell */}
                    <td className="px-6 py-4 whitespace-nowrap text-xs  text-ds-gray-600">
                      {rowHeader.label}
                    </td>

                    {/* Data cells */}
                    {sortedColumnHeaders.map((colHeader) => {
                      const cellValue = rowValues.find((v) => v.column_id === colHeader.column_id);
                      return (
                        <td
                          key={colHeader.column_id}
                          className="px-6 py-4 whitespace-nowrap text-xs text-ds-gray-600"
                        >
                          {cellValue ? formatValue(cellValue.value) : 'N/A'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}

              {/* Fallback for tables without row headers */}
              {(!tableData.rowHeaders || tableData.rowHeaders.length === 0) &&
                Object.keys(groupedValues).map((rowId, rowIndex) => {
                  const rowValues = groupedValues[rowId];
                  const rowLabel = `Row ${rowIndex + 1}`;

                  return (
                    <tr key={rowId} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {rowLabel}
                      </td>
                      {sortedColumnHeaders.map((colHeader) => {
                        const cellValue = rowValues.find(
                          (v) => v.column_id === colHeader.column_id
                        );
                        return (
                          <td
                            key={colHeader.column_id}
                            className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"
                          >
                            {cellValue ? formatValue(cellValue.value) : 'N/A'}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DynamicTable;
